import { logger } from "../../utils/logger";
import { Target, Award, Users, Shield, Check, Briefcase, Globe2, TrendingUp, Zap, Clock, Star, Rocket, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { homepageAPI } from '../../utils/api';

interface WhyChooseFeature {
  id: string;
  icon: string;
  title: string;
  description: string;
  stat: string;
  statLabel: string;
  badge: string;
  color: string;
  badgeColor: string;
  badgeText: string;
  badgeBorder: string;
  order: number;
}

interface TrustIndicator {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  order: number;
}

interface WhyChooseSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  title: string;
  description: string;
  visible: boolean;
  features: WhyChooseFeature[];
  trustIndicators: TrustIndicator[];
  trustIndicatorsVisible: boolean;
  updatedAt: string;
}

const iconMap: { [key: string]: any } = {
  Target, Award, Users, Globe2, Shield, Check,
  Briefcase, TrendingUp, Zap, Clock, Star, Rocket, CheckCircle,
};

// ============================================
// SKELETON
// ============================================

const WhyChooseUsSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-6xl mx-auto">
      {/* Badge + title */}
      <div className="text-center mb-12 space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-56 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="space-y-2 max-w-xl mx-auto">
          <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-4/5 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
      </div>

      {/* Feature cards grid — 4 columns matching real layout */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white/80 rounded-2xl p-6 shadow-md border border-white/40 space-y-4">
            {/* Badge pill */}
            <div className="h-5 w-28 bg-gray-200 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Icon */}
            <div className="h-14 w-14 bg-gray-200 rounded-xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Stat */}
            <div className="space-y-1">
              <div className="h-8 w-16 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-3 w-24 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
            {/* Title + description */}
            <div className="space-y-2">
              <div className="h-4 w-3/4 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-3 w-full bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-3 w-5/6 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Trust indicators bar */}
      <div className="bg-white/80 rounded-2xl p-8 shadow-md border border-white/40">
        <div className="grid sm:grid-cols-3 gap-8">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="text-center space-y-3">
              <div className="flex justify-center">
                <div className="h-10 w-10 bg-gray-200 rounded-lg relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="h-4 w-24 bg-gray-200 rounded mx-auto relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-3 w-36 bg-gray-200 rounded mx-auto relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function WhyChooseUsCreative() {
  const [data, setData] = useState<WhyChooseSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchData = async () => {
    try {
      const result = await homepageAPI.getWhyChoose();
      if (result.success && result.data) {
        setData(result.data);
      } else {
        setData(getDefaultData());
      }
    } catch (error) {
      logger.error('❌ [WHY CHOOSE] Fetch error:', error instanceof Error ? error.message : String(error));
      setData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultData = (): WhyChooseSection => ({
    id: 'why-choose-main',
    badgeText: 'Why Top Companies Choose Us',
    badgeIcon: 'Shield',
    title: 'Trusted Excellence in Every Frame',
    description: 'Working with Fortune 500 companies and innovative startups worldwide—delivering animation that drives measurable business results',
    visible: true,
    features: [
      {
        id: 'feature-1',
        icon: 'Target',
        title: 'Industry Expertise',
        description: 'Deep knowledge of complex B2B products across manufacturing, energy, and automotive sectors',
        stat: '500+',
        statLabel: 'Projects Delivered',
        badge: 'Proven Track Record',
        color: 'from-[#E87D3E] to-[#d66b2a]',
        badgeColor: 'from-orange-50 to-orange-100/80',
        badgeText: 'text-[#E87D3E]',
        badgeBorder: 'border-orange-200/50',
        order: 1
      },
      {
        id: 'feature-2',
        icon: 'Award',
        title: 'Premium Quality',
        description: 'Award-winning animations that meet the highest standards of technical accuracy and visual appeal',
        stat: '4.9/5',
        statLabel: 'Client Rating',
        badge: 'Excellence Guaranteed',
        color: 'from-[#E8B84D] to-[#d4a03d]',
        badgeColor: 'from-amber-50 to-amber-100/80',
        badgeText: 'text-[#E8B84D]',
        badgeBorder: 'border-amber-200/50',
        order: 2
      },
      {
        id: 'feature-3',
        icon: 'Users',
        title: 'Expert Team',
        description: 'Dedicated specialists who understand technical complexity and translate it into clear visuals',
        stat: '25+',
        statLabel: 'Specialists',
        badge: 'Expert Creators',
        color: 'from-[#2F2E5C] to-[#1a1935]',
        badgeColor: 'from-indigo-50 to-indigo-100/80',
        badgeText: 'text-[#2F2E5C]',
        badgeBorder: 'border-indigo-200/50',
        order: 3
      },
      {
        id: 'feature-4',
        icon: 'Globe2',
        title: 'Global Reach',
        description: 'Supporting clients worldwide with multilingual capabilities and 24/7 project management',
        stat: '40+',
        statLabel: 'Countries Served',
        badge: 'International',
        color: 'from-[#D74444] to-[#b93636]',
        badgeColor: 'from-red-50 to-red-100/80',
        badgeText: 'text-[#D74444]',
        badgeBorder: 'border-red-200/50',
        order: 4
      }
    ],
    trustIndicators: [
      { id: 'trust-1', icon: 'Shield', title: 'NDA Protection', description: 'Strict confidentiality for all projects', color: 'from-[#E87D3E] to-[#d66b2a]', order: 1 },
      { id: 'trust-2', icon: 'Shield', title: 'ISO Compliant', description: 'Quality standards you can trust', color: 'from-[#E8B84D] to-[#d4a03d]', order: 2 },
      { id: 'trust-3', icon: 'Briefcase', title: '10+ Years', description: 'Established industry expertise', color: 'from-[#2F2E5C] to-[#1a1935]', order: 3 }
    ],
    trustIndicatorsVisible: true,
    updatedAt: new Date().toISOString()
  });

  const getIconComponent = (iconName: string) => iconMap[iconName] || Target;

  if (loading) {
    return <WhyChooseUsSkeleton />;
  }

  if (!data || !data.visible) {
    return null;
  }

  const BadgeIcon = getIconComponent(data.badgeIcon);

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Premium Matte Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-[#2F2E5C] via-[#4A4878] to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{data.badgeText}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
            {data.title}
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {data.description}
          </p>
        </div>
        
        {/* Main Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {data.features.map((item) => {
            const ItemIcon = getIconComponent(item.icon);
            return (
              <div key={item.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-md border border-white/40 hover:shadow-xl transition-all hover:-translate-y-2 group relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${item.color} opacity-5 rounded-bl-full`} />
                
                <div className={`inline-block bg-gradient-to-r ${item.badgeColor} backdrop-blur-sm px-2.5 py-1 rounded-full mb-4 border ${item.badgeBorder}`}>
                  <span className={`text-xs font-bold ${item.badgeText}`}>{item.badge}</span>
                </div>

                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform shadow-lg border border-white/10`}>
                  <ItemIcon className="w-6 h-6" />
                </div>
                
                <div className="mb-4">
                  <div className="text-3xl font-bold text-[#2F2E5C] mb-1">{item.stat}</div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.statLabel}</div>
                </div>
                
                <h3 className="font-bold mb-2 text-[#2F2E5C]">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        {data.trustIndicatorsVisible && data.trustIndicators.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-md border border-white/40">
            <div className="grid sm:grid-cols-3 gap-8">
              {data.trustIndicators.map((indicator) => {
                const IndicatorIcon = getIconComponent(indicator.icon);
                return (
                  <div key={indicator.id} className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${indicator.color} rounded-lg flex items-center justify-center shadow-lg border border-white/20`}>
                        <IndicatorIcon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="font-bold text-[#2F2E5C] mb-1">{indicator.title}</div>
                    <p className="text-sm text-gray-600">{indicator.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}